// Copyright ©️ 2026 eVolve MEP, LLC

import './spoolsPanel.css';

import { notifications } from '@mantine/notifications';

import { CloudSpoolingHelpLink, EvolveWebSpoolView } from 'components/Autodesk/constants';
import type { SpoolsStore } from 'components/Autodesk/extensions/SpoolsExtension/createSpoolsStore';
import { SpoolLoader } from 'components/Autodesk/extensions/SpoolsExtension/SpoolLoader';
import { addViewerListener, findDocumentNode } from 'components/Autodesk/extensions/utils';
import type { MountPortalFn } from 'components/Autodesk/types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';

import { SpoolSelector } from './SpoolSelector';

const { Autodesk } = window;

/** An extension to view and isolate all Spools in a model. */
export class SpoolsExtension extends Autodesk.Viewing.Extension {
  urn: string;
  /** What spool name to automatically isolate on model load */
  autoSelectSpool?: string;
  /** Loader objects used to cache spools, spool requests, and machine elements */
  spoolLoader: SpoolLoader;
  /** Used to track if we've successfully isolated `autoSelectSpool` since loading the Extension */
  autoSpoolIsolated: 'initial' | 'view-loaded' | 'isolated' | 'canceled' = 'initial';

  /** DockingPanel to render our SpoolSelector component */
  panel: Autodesk.Viewing.UI.DockingPanel | undefined;
  /** If true, will show the SpoolSelector menu in the toolbar */
  showSpoolMenu: boolean;
  /** List of clean up methods to call when the extension is unloaded */
  cleanUpFns: (() => void)[] = [];
  /** Portal cleanup method, separate from the other clean-up methods so we can detect if the portal is mounted */
  private cleanUpPortal: (() => void) | undefined;
  private readonly spoolStore: SpoolsStore;
  private readonly mountPortal: MountPortalFn;

  constructor(
    viewer: Autodesk.Viewing.GuiViewer3D,
    opts: {
      urn: string;
      autoSelectSpool?: string;
      showSpoolMenu?: boolean;
      spoolStore: SpoolsStore;
      mountPortal: MountPortalFn;
    },
  ) {
    super(viewer, opts);

    this.spoolStore = opts.spoolStore;
    this.mountPortal = opts.mountPortal;

    this.spoolLoader = new SpoolLoader(viewer, this.spoolStore);

    this.clearSpools = this.clearSpools.bind(this);
    this.createUI = this.createUI.bind(this);

    this.urn = opts.urn;
    this.autoSelectSpool = opts.autoSelectSpool;
    this.showSpoolMenu = opts.showSpoolMenu ?? true;
  }

  // Getter to make accessing this.spoolLoader.spools easier; you can use this.spools instead of this.spoolLoader.spools
  get spools() {
    return this.spoolLoader.spools;
  }

  load() {
    this.cleanUpFns.push(addViewerListener(this.viewer, Autodesk.Viewing.MODEL_UNLOADED_EVENT, this.clearSpools));

    // model will not exist for certain file types
    if (isNotNil(this.viewer.model)) {
      let viewChanging = false;
      // If a spool name was passed in, then we want to isolate that specific spool.
      if (this.autoSelectSpool && this.autoSpoolIsolated === 'initial') {
        // If the EVOLVE Web Spool view is already loaded, no need to find another view
        const evolveSpoolViewIsLoaded = this.viewer.model.getDocumentNode().data.name === EvolveWebSpoolView;
        if (!evolveSpoolViewIsLoaded) {
          // If it isn't loaded, try to find a view that matches the name of our spool
          const spoolView = findDocumentNode(
            this.viewer.model.getDocumentNode().getRootNode(),
            (n) => n.data.name === this.autoSelectSpool && n.data.type === 'geometry',
          );
          // If we found a document, load that document specifically
          if (isNotNil(spoolView)) {
            // making sure it isn't the document that's already loaded.
            if (spoolView.getDocument() !== this.viewer.model.getDocumentNode().getDocument()) {
              viewChanging = true;
              this.viewer.getAllModels().forEach((model) => this.viewer.unloadModel(model));
              // The DocumentBrowser can't handle the active document changing externally,
              // so we unload it and then re-load it after changing the active view
              const docBrowserWasLoaded = this.viewer.unloadExtension('Autodesk.DocumentBrowser');
              setTimeout(() => {
                // Delay long enough to prevent race conditions in the Forge Viewer
                // TODO: Delaying isn't *always* needed; why?
                void this.viewer
                  .loadDocumentNode(spoolView.getDocument(), spoolView.getDefaultGeometry())
                  .then(async () => {
                    if (docBrowserWasLoaded) await this.viewer.loadExtension('Autodesk.DocumentBrowser');
                  });
              }, 500);
            }
          } else {
            notifications.show({
              title: 'Warning: Unable to locate Spool view',
              message: (
                <>
                  Could not find a view named &quot;{EvolveWebSpoolView}&quot;
                  <br />
                  <a className="link" href={CloudSpoolingHelpLink} target="_blank" rel="noreferrer">
                    Need help with Web Spooling?
                  </a>
                </>
              ),
              color: 'goldenrod',
              autoClose: false,
            });
          }
        }
        this.autoSpoolIsolated = 'view-loaded';
      }

      if (!viewChanging && this.viewer.model.is3d()) {
        const watchdog = window.setTimeout(() => {
          // oxlint-disable-next-line no-console
          console.warn('Spools have not been realized after 15 seconds. This may be due to a large model.');
        }, 15000);
        void this.spoolLoader
          .loadSpools(this.viewer.model)
          .then((spools) => {
            // Attempt to isolate the spool within the loaded document
            if (this.autoSelectSpool && this.autoSpoolIsolated === 'view-loaded') {
              const spoolDbIds = spools[this.autoSelectSpool]?.objects.map(({ dbId }) => dbId) ?? [];
              if (spoolDbIds.length > 0) {
                this.viewer.isolate(spoolDbIds);
                this.viewer.fitToView(spoolDbIds);
                this.autoSpoolIsolated = 'isolated';
              } else {
                // If we couldn't isolate the spool, mark it as undefined
                // so we don't try again when the user swaps views
                // FIXME: This probably doesn't work due to how Forge Viewer unloads extensions during view swaps
                this.autoSelectSpool = undefined;
                this.autoSpoolIsolated = 'canceled';
              }
            }
            if (strongKeys(spools).length > 0 && this.showSpoolMenu) {
              this.createUI();
            }
            this.autoSpoolIsolated = 'canceled';
          })
          .catch((e) => {
            // oxlint-disable-next-line no-console
            console.error(e);
            notifications.show({
              title: 'Something went wrong',
              message: 'Could not extract spools from model. Please contact support.',
              color: 'red',
            });
          })
          .finally(() => {
            window.clearTimeout(watchdog);
          });
      }
    }
    return true;
  }

  unload() {
    this.cleanUpPortal?.();
    this.cleanUpPortal = undefined;

    if (this.panel) {
      this.panel.setVisible(false);
      this.viewer.removePanel(this.panel);
      this.panel.uninitialize();
      this.panel = undefined;
    }
    this.spoolLoader.clearSpools();
    this.cleanUpFns.forEach((fn) => fn());
    this.cleanUpFns = [];

    return true;
  }

  /** on model change, reset the spools */
  clearSpools() {
    this.spoolLoader.clearSpools();
  }

  createUI() {
    if (isNotNil(this.cleanUpPortal)) return;

    const toolbarGroup = this.viewer.toolbar.getControl('modelTools') as Autodesk.Viewing.UI.ControlGroup;
    if (isNil(toolbarGroup)) return;

    this.panel = new Autodesk.Viewing.UI.DockingPanel(this.viewer.container, 'SpoolPanel', 'Spools');
    const button = new Autodesk.Viewing.UI.Button('SpoolSelector');
    button.setToolTip('Spools');
    button.icon.classList.add('fas', 'fa-cubes');

    this.panel.createScrollContainer({});
    // oxlint-disable-next-line typescript/no-unsafe-call, typescript/no-unsafe-member-access
    this.panel.container.classList.add('spoolSelectorPanel');

    this.panel.addVisibilityListener((visible) => {
      if (!visible) {
        button.removeClass('active');
        button.addClass('inactive');
      } else {
        button.removeClass('inactive');
        button.addClass('active');
      }
    });

    button.onClick = () => this.panel?.setVisible(!this.panel.isVisible());

    toolbarGroup.addControl(button);
    this.viewer.addPanel(this.panel);
    this.cleanUpPortal = this.mountPortal(this.panel.scrollContainer, <SpoolSelector />);
  }
}
